import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Users, Clock, CheckCircle2 } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { NotificationsBell } from "@/components/NotificationsBell";

interface Gig {
  id: string;
  title: string;
  status: string;
  budget_cents: number;
  workers_needed: number;
  start_time: string | null;
  created_at: string;
  applicants?: number;
  active_shifts?: number;
}

const BusinessDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("gigs")
      .select("id, title, status, budget_cents, workers_needed, start_time, created_at")
      .eq("poster_id", user.id)
      .order("created_at", { ascending: false });
    const list = (data as Gig[]) ?? [];
    if (list.length) {
      const ids = list.map((g) => g.id);
      const [{ data: apps }, { data: shifts }] = await Promise.all([
        supabase.from("gig_applications").select("gig_id, status").in("gig_id", ids),
        supabase.from("gig_shifts").select("gig_id, status").in("gig_id", ids),
      ]);
      const appMap = new Map<string, number>();
      (apps ?? []).forEach((a: any) => {
        if (a.status === "pending") appMap.set(a.gig_id, (appMap.get(a.gig_id) ?? 0) + 1);
      });
      const shiftMap = new Map<string, number>();
      (shifts ?? []).forEach((s: any) => {
        if (s.status === "in_progress") shiftMap.set(s.gig_id, (shiftMap.get(s.gig_id) ?? 0) + 1);
      });
      list.forEach((g) => {
        g.applicants = appMap.get(g.id) ?? 0;
        g.active_shifts = shiftMap.get(g.id) ?? 0;
      });
    }
    setGigs(list);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate("/wallet")} right={<NotificationsBell />} />
        <div className="grid place-items-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MobileShell>
    );

  const open = gigs.filter((g) => g.status === "open");
  const active = gigs.filter((g) => g.status === "in_progress" || g.status === "pending_approval");
  const closed = gigs.filter((g) => ["completed", "cancelled", "disputed"].includes(g.status));

  const Card = ({ g }: { g: Gig }) => (
    <Link
      to={`/gigs/${g.id}`}
      className="block rounded-2xl bg-card border border-border p-4 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-display font-bold text-base leading-tight">{g.title}</p>
        <span className="font-display font-extrabold text-primary text-sm">
          {formatMoney(g.budget_cents)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="uppercase font-semibold">{g.status.replace(/_/g, " ")}</span>
        {g.start_time && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {new Date(g.start_time).toLocaleString()}
          </span>
        )}
        {!!g.applicants && (
          <span className="flex items-center gap-1 text-primary font-semibold">
            <Users className="h-3 w-3" /> {g.applicants} new
          </span>
        )}
        {!!g.active_shifts && (
          <span className="flex items-center gap-1 text-accent font-semibold">
            <CheckCircle2 className="h-3 w-3" /> {g.active_shifts} on shift
          </span>
        )}
      </div>
    </Link>
  );

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate("/wallet")} right={<NotificationsBell />} />
      <div className="px-4 pt-2 pb-24">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-display font-extrabold text-2xl">Hustle Hub</h1>
          <Button size="sm" className="rounded-full" onClick={() => navigate("/gigs/new")}>
            <Plus className="h-4 w-4 mr-1" /> Post job
          </Button>
        </div>
        <Tabs defaultValue="open">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="open">Open ({open.length})</TabsTrigger>
            <TabsTrigger value="active">Active ({active.length})</TabsTrigger>
            <TabsTrigger value="closed">Closed ({closed.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="open" className="space-y-3 mt-3">
            {open.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No open jobs.</p>
            ) : (
              open.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
          <TabsContent value="active" className="space-y-3 mt-3">
            {active.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No active jobs.</p>
            ) : (
              active.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
          <TabsContent value="closed" className="space-y-3 mt-3">
            {closed.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No closed jobs.</p>
            ) : (
              closed.map((g) => <Card key={g.id} g={g} />)
            )}
          </TabsContent>
        </Tabs>
      </div>
    </MobileShell>
  );
};

export default BusinessDashboard;
